import {
    Button,
    Link,
    LinkProps,
    makeStyles,
    Toast,
    ToastBody,
    Toaster,
    ToastFooter,
    ToastIntent,
    ToastTitle,
    ToastTrigger,
    tokens,
    useId,
    useToastController,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { createContext, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

export interface IToastMessageContextValues {
    showToast: (
        title: string | React.ReactNode,
        body: string | React.ReactNode,
        intent?: ToastIntent,
        links?: ToastLinks[],
        timeoutInMilliseconds?: number
    ) => void;
}

export interface ToastLinks {
    text: string;
    linkProps: LinkProps;
}
const useStyles = makeStyles({
    toast: {
        '& svg': { width: '20px', height: '20px', paddingTop: 0 },
        '>.fui-ToastTitle__action': {
            paddingLeft: 0,
        },
        '>.fui-ToastTitle__media': {
            padding: '0px 5px 0px 0px ',
        },
    },
    toastTitle: {
        color: tokens.colorNeutralForeground1,
    },
    toastBody: {
        paddingTop: 0,
        lineHeight: tokens.lineHeightBase300,
    },
    toastFooter: {
        paddingTop: '7px',
    },
    closeIcon: {
        height: '25px',
    },
});

export const ToastMessageContext = createContext<IToastMessageContextValues | null>(null);

export const TOAST_DEFAULT_TIMEOUT = 5000;

export const ToastMessageContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const toasterId = useId('corptax-toaster');
    const { t } = useTranslation();
    const { toast, toastTitle, toastBody, toastFooter, closeIcon } = useStyles();
    const { dispatchToast } = useToastController(toasterId);

    const showToast = (
        title: string | React.ReactNode,
        body: string | React.ReactNode,
        intent: ToastIntent = 'success',
        links?: ToastLinks[],
        timeoutInMilliseconds = TOAST_DEFAULT_TIMEOUT
    ) => {
        dispatchToast(
            <Toast className={toast}>
                <ToastTitle
                    className={toastTitle}
                    action={
                        <ToastTrigger>
                            <Button
                                appearance='subtle'
                                className={closeIcon}
                                icon={<Dismiss24Regular />}
                                aria-label={t('closeModal').toString()}
                            />
                        </ToastTrigger>
                    }
                >
                    {title}
                </ToastTitle>
                <ToastBody className={toastBody}>{body}</ToastBody>
                <ToastFooter className={toastFooter}>
                    {links?.map((link) => (
                        <Link key={link.text} {...link.linkProps}>
                            {link.text}
                        </Link>
                    ))}
                </ToastFooter>
            </Toast>,
            {
                intent,
                timeout: timeoutInMilliseconds,
            }
        );
    };

    return (
        <ToastMessageContext.Provider value={{ showToast }}>
            <Toaster pauseOnWindowBlur pauseOnHover toasterId={toasterId} />
            {children}
        </ToastMessageContext.Provider>
    );
};
